import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, Search, Trash2, UserPlus, Upload, Lock, UserCog } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { api, errMsg } from "../lib/api";
import { Btn } from "./common";

export const isVisible = (f, d) => !f.show_if || f.show_if.some((c) => {
  const v = d[c.field];
  return (c.in && c.in.includes(v)) || (c.has && Array.isArray(v) && v.includes(c.has)) || (c.any_except && Array.isArray(v) && v.some((x) => !c.any_except.includes(x)));
});

export const calcAge = (bd) => {
  if (!bd) return null;
  const b = new Date(bd), t = new Date();
  let a = t.getFullYear() - b.getFullYear();
  if (t.getMonth() < b.getMonth() || (t.getMonth() === b.getMonth() && t.getDate() < b.getDate())) a -= 1;
  return Number.isNaN(a) ? null : a;
};

export const groupSections = (fields) => {
  const out = [];
  fields.forEach((f) => {
    const s = f.section || "Details";
    const g = out.find((x) => x[0] === s);
    g ? g[1].push(f) : out.push([s, [f]]);
  });
  return out;
};

export function MultiSelect({ f, value, onChange, disabled }) {
  const sel = Array.isArray(value) ? value : [];
  const toggle = (o) => {
    let next = sel.includes(o) ? sel.filter((x) => x !== o) : [...sel, o];
    if (f.exclusive && o === f.exclusive && !sel.includes(o)) next = [o];
    else if (f.exclusive && o !== f.exclusive) next = next.filter((x) => x !== f.exclusive);
    onChange(next);
  };
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button type="button" disabled={disabled} data-testid={`field-${f.key}`} className="w-full min-h-[38px] rounded-md border border-slate-300 bg-white px-3 py-2 text-left text-sm flex items-center justify-between gap-2 disabled:bg-slate-100">
          <span className={`truncate ${sel.length ? "" : "text-slate-400"}`}>{sel.length ? sel.join(", ") : "Select…"}</span>
          <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-80 max-h-72 overflow-y-auto p-2" align="start">
        {f.options.map((o) => (
          <label key={o} className="flex items-start gap-2 rounded px-2 py-1.5 text-sm hover:bg-slate-50 cursor-pointer">
            <input type="checkbox" checked={sel.includes(o)} onChange={() => toggle(o)} className="mt-0.5 accent-[var(--brand-primary)]" data-testid={`field-${f.key}-opt-${f.options.indexOf(o)}`} />{o}
          </label>
        ))}
      </PopoverContent>
    </Popover>
  );
}

export function AuthImage({ url, alt, testid }) {
  const [src, setSrc] = useState(null);
  const [isPdf, setIsPdf] = useState(false);
  useEffect(() => {
    let u;
    api.get(url, { responseType: "blob" }).then((r) => { u = URL.createObjectURL(r.data); setIsPdf(r.data.type === "application/pdf"); setSrc(u); }).catch(() => setSrc(null));
    return () => { if (u) URL.revokeObjectURL(u); };
  }, [url]);
  if (!src) return <span className="text-xs text-slate-400">No preview</span>;
  return isPdf ? <a href={src} target="_blank" rel="noreferrer" className="text-xs text-brand underline" data-testid={testid}>Open PDF</a>
    : <img src={src} alt={alt} className="h-24 w-24 rounded border object-cover" data-testid={testid} />;
}

export function ImageField({ f, value, onChange, module, record, disabled, onBusy }) {
  const [local, setLocal] = useState(null);
  const [busy, setBusyState] = useState(false);
  const setBusy = (b) => { setBusyState(b); onBusy?.(b); };
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try {
      const r = await api.post(`/m/${module.key}/field-upload`, fd);
      onChange(r.data.file_id);
      setLocal(file.type.startsWith("image/") ? URL.createObjectURL(file) : "pdf");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const saved = record && record.data[f.key] && value === record.data[f.key];
  return (
    <div className="flex items-center gap-3">
      {local && local !== "pdf" ? <img src={local} alt="" className="h-16 w-16 rounded border object-cover" /> : local === "pdf" ? <span className="text-xs">PDF selected</span>
        : saved ? <AuthImage url={`/m/${module.key}/records/${record.id}/field-file/${f.key}`} alt={f.label} /> : null}
      <label className={`inline-flex items-center gap-2 rounded-md border px-3 py-2 text-xs font-medium ${disabled ? "opacity-50" : "cursor-pointer hover:bg-slate-50"}`}>
        <Upload className="h-3.5 w-3.5" />{busy ? "Uploading…" : value ? "Replace" : "Upload"}
        <input type="file" accept="image/png,image/jpeg,application/pdf" className="hidden" disabled={disabled || busy} onChange={(e) => upload(e.target.files[0])} data-testid={`field-${f.key}`} />
      </label>
    </div>
  );
}

function ResidentPicker({ open, onClose, onPick, excludeIds, householdId, needRelationship, relationships, title }) {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [picked, setPicked] = useState(null);
  const [rel, setRel] = useState("");
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => api.get("/residents/lookup", { params: { q, household_id: householdId || "" } }).then((r) => setRows(r.data)).catch(() => setRows([])), 250);
    return () => clearTimeout(t);
  }, [q, open, householdId]);
  useEffect(() => { if (open) { setPicked(null); setRel(""); setQ(""); } }, [open]);
  const list = rows.filter((r) => r.eligible && !excludeIds.includes(r.id));
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="resident-picker-dialog">
        <DialogHeader><DialogTitle>{title}</DialogTitle><DialogDescription>Search the Inhabitants Registry. Residents already linked to another household are excluded.</DialogDescription></DialogHeader>
        <div className="relative"><Search className="absolute left-3 top-2.5 h-4 w-4 text-slate-400" />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or Resident ID…" className="w-full rounded-md border pl-9 pr-3 py-2 text-sm" data-testid="resident-picker-search" /></div>
        <div className="max-h-64 overflow-y-auto rounded-md border divide-y" data-testid="resident-picker-results">
          {list.length === 0 && <p className="p-3 text-sm text-slate-500">No eligible residents found.</p>}
          {list.map((r) => (
            <button type="button" key={r.id} onClick={() => (needRelationship ? setPicked(r) : onPick(r))} data-testid={`resident-option-${r.ref_no}`}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-[var(--brand-soft)] ${picked?.id === r.id ? "bg-[var(--brand-soft)]" : ""}`}>
              <span className="font-medium">{r.name}</span> <span className="font-mono text-xs text-slate-500">{r.ref_no}</span>
              <span className="block text-xs text-slate-500">{r.birth_date || "—"} · Age {r.age ?? "—"} · {r.gender || "—"}</span>
            </button>
          ))}
        </div>
        {needRelationship && picked && (
          <div className="flex flex-wrap items-end gap-2">
            <div className="flex-1"><label className="text-xs font-medium" htmlFor="rel-select">Relationship to Head of the Family *</label>
              <select id="rel-select" value={rel} onChange={(e) => setRel(e.target.value)} className="w-full rounded-md border px-3 py-2 text-sm" data-testid="relationship-select">
                <option value="">Select…</option>{relationships.map((o) => <option key={o}>{o}</option>)}</select></div>
            <Btn type="button" disabled={!rel} onClick={() => onPick(picked, rel)} data-testid="add-member-confirm-btn">Add {picked.name}</Btn>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function FamilyEditor({ module, record, data, setData, disabled }) {
  const [rows, setRows] = useState([]);
  const [picker, setPicker] = useState(null);
  const relField = module.fields.find((f) => f.key === "members");
  useEffect(() => {
    if (!record) return;
    api.get(`/households/${record.id}/family`).then((r) => {
      setRows(r.data);
      const head = r.data.find((x) => x.is_head);
      setData((d) => ({ ...d, head_resident_id: head?.resident_id, members: r.data.filter((x) => !x.is_head).map((x) => ({ resident_id: x.resident_id, relationship: x.relationship })) }));
    });
  }, [record, setData]);
  const sync = (next) => {
    setRows(next);
    const head = next.find((x) => x.is_head);
    setData((d) => ({ ...d, head_resident_id: head?.resident_id, members: next.filter((x) => !x.is_head).map((x) => ({ resident_id: x.resident_id, relationship: x.relationship })) }));
  };
  const toRow = (r, rel, isHead) => ({ resident_id: r.id, ref_no: r.ref_no, name: r.name, birth_date: r.birth_date, age: r.age, gender: r.gender, occupation: r.occupation, relationship: rel, is_head: isHead });
  const head = rows.find((x) => x.is_head);
  const headLocked = record && head && !module.can_change_head;
  return (
    <div className="sm:col-span-2 space-y-3" data-testid="family-editor">
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border bg-slate-50 p-3">
        <div><p className="text-xs font-medium text-slate-700">Head of the Family {!disabled && <span className="text-red-600">*</span>}</p>
          <p className="text-sm" data-testid="selected-head">{head ? <><b>{head.name}</b> <span className="font-mono text-xs text-slate-500">{head.ref_no}</span></> : <span className="text-slate-400">Not selected</span>}</p></div>
        {!disabled && (headLocked ? <span className="text-xs text-slate-500 flex items-center gap-1"><Lock className="h-3 w-3" />Head change requires Secretary/Captain</span>
          : <Btn type="button" variant="outline" className="text-xs" onClick={() => setPicker("head")} data-testid="select-head-btn"><UserCog className="h-3.5 w-3.5" />{head ? "Change head" : "Select head"}</Btn>)}
      </div>
      <div className="flex items-center justify-between"><p className="text-xs font-medium text-slate-700">Family table · {rows.length} member(s) incl. head</p>
        {!disabled && <Btn type="button" variant="outline" className="text-xs" disabled={!head} onClick={() => setPicker("member")} data-testid="add-family-member-btn"><UserPlus className="h-3.5 w-3.5" />Add family member</Btn>}</div>
      <div className="overflow-x-auto rounded-md border"><table className="w-full text-xs" data-testid="family-table">
        <thead className="bg-slate-50 text-left uppercase text-slate-500"><tr>{["Full name", "Birthdate", "Age", "Sex", "Occupation", "Relationship", "Action"].map((h) => <th key={h} className="p-2">{h}</th>)}</tr></thead>
        <tbody className="divide-y">
          {rows.length === 0 && <tr><td colSpan={7} className="p-3 text-slate-500">Select the head of the family first.</td></tr>}
          {rows.map((r) => (
            <tr key={r.resident_id} data-testid={`family-row-${r.ref_no}`}><td className="p-2 font-medium">{r.name}</td><td className="p-2">{r.birth_date || "—"}</td><td className="p-2">{r.age ?? "—"}</td>
              <td className="p-2">{r.gender || "—"}</td><td className="p-2">{r.occupation || "—"}</td><td className="p-2">{r.relationship}</td>
              <td className="p-2">{r.is_head || disabled ? <span className="text-slate-400">—</span> : <button type="button" aria-label={`Remove ${r.name}`} onClick={() => sync(rows.filter((x) => x.resident_id !== r.resident_id))} className="text-red-700" data-testid={`remove-member-${r.ref_no}`}><Trash2 className="h-4 w-4" /></button>}</td></tr>
          ))}
        </tbody></table></div>
      <ResidentPicker open={!!picker} onClose={() => setPicker(null)} householdId={record?.id} relationships={relField.options}
        title={picker === "head" ? "Select Head of the Family" : "Add family member"} needRelationship={picker === "member"}
        excludeIds={rows.map((x) => x.resident_id)}
        onPick={(r, rel) => {
          if (picker === "head") sync([toRow(r, "Head of the Family", true), ...rows.filter((x) => !x.is_head)]);
          else sync([...rows, toRow(r, rel, false)]);
          setPicker(null);
        }} />
    </div>
  );
}
